import type { FC } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Voyage } from '../../core/_models'
import type { QHSEDefectDto } from '../../../QHSE/core/_models'
import type { DashboardVoyage, PortConsumptionRow } from './types'
import { PortConsumptionTable } from './ConsumptionTable'
import { useOperationsAccess } from '../../core/useOperationsAccess'
import { OP } from '../../core/operationPermissions'
import { DPR_ROUTE } from '../../core/routes'
import DashboardModal from './DashboardModal'
import type { ActionButton } from './DashboardModal'
import type { SummaryMetric } from './types'
import { isOpenDashboardDefect, dashboardReportName, upcomingDashboardVoyages } from './dashboardData'

/* ─── Helpers ─── */
function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${(/^[=+@\t\r-]/.test(c) ? "\'" + c : c).replace(/"/g, '""')}"`).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

/* ────────────── Voyage Detail Modal ────────────── */
type VoyageDetailModalProps = {
  open: boolean
  onClose: () => void
  voyages: Voyage[]
  title?: string
  locationStatus?: 'At Sea' | 'In Port'
}
const VoyageDetailModal: FC<VoyageDetailModalProps> = ({ open, onClose, voyages, title = 'Active Voyages', locationStatus }) => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = voyages.filter((v) =>
    (v.vessel?.fleet_name || '').toLowerCase().includes(q) ||
    (v.voyageNumber || '').toLowerCase().includes(q) ||
    (v.departurePort || '').toLowerCase().includes(q) ||
    (v.arrivalPort || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => downloadCsv('active-voyages.csv',
      ['Vessel', 'Voyage #', 'From', 'To', 'Status', 'Dates'],
      filtered.map((v) => [v.vessel?.fleet_name || '', v.voyageNumber || '', v.departurePort || '', v.arrivalPort || '', locationStatus || (v.active ? 'Active' : 'Completed'), `${v.startDate ? new Date(v.startDate).toLocaleDateString() : ''} – ${v.endDate ? new Date(v.endDate).toLocaleDateString() : ''}`]),
    )},
    { label: 'Go to Voyages', variant: 'primary', onClick: () => { onClose(); navigate('/operations/voyages') } },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title={title} wide
      actions={actions.filter(action => action.label !== 'Download CSV' || (permissionsLoaded && opCan(OP.DOWNLOAD_VOYAGE) && (!locationStatus || [OP.DOWNLOAD_POSITION_REPORT, OP.DOWNLOAD_DAILY_PROGRESS_REPORT].every(opCan))))}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel, voyage or port...'
    >
      {locationStatus && <p className='text-muted'>{new Set(filtered.map(v => v.vessel?.id)).size} vessels · {filtered.length} active voyages reported {locationStatus}. Vessels with multiple active voyages appear once per voyage.</p>}
      <div className='table-responsive'>
        <table className='table table-sm align-middle mb-0'>
          <thead>
            <tr style={{ fontSize: '10px' }} className='text-muted text-uppercase'>
              <th className='fw-semibold'>Vessel</th>
              <th className='fw-semibold'>Voyage #</th>
              <th className='fw-semibold'>From</th>
              <th className='fw-semibold'>To</th>
              <th className='fw-semibold'>Status</th>
              <th className='fw-semibold'>Dates</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '11px' }}>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className='text-center text-muted py-4'>No voyages found</td></tr>
            ) : (
              filtered.map((v, i) => (
                <tr key={i}>
                  <td className='fw-semibold text-dark'>{v.vessel?.fleet_name || '—'}</td>
                  <td>{v.voyageNumber || '—'}</td>
                  <td>{v.departurePort || '—'}</td>
                  <td>{v.arrivalPort || '—'}</td>
                  <td>
                    <span className={`badge ${v.active ? 'badge-light-success' : 'badge-light-secondary'}`} style={{ fontSize: '9px' }}>
                      {locationStatus || (v.active ? 'Active' : 'Completed')}
                    </span>
                  </td>
                  <td className='text-muted'>{v.startDate ? new Date(v.startDate).toLocaleDateString() : '—'} – {v.endDate ? new Date(v.endDate).toLocaleDateString() : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ────────────── Defect Detail Modal ────────────── */
type DefectDetailModalProps = {
  open: boolean
  onClose: () => void
  defects: QHSEDefectDto[]
  title?: string
  overdue?: boolean
}
const DefectDetailModal: FC<DefectDetailModalProps> = ({ open, onClose, defects, title = 'Open Defect Reports', overdue }) => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = defects.filter((d) =>
    (d.vesselName || '').toLowerCase().includes(q) ||
    (d.description || '').toLowerCase().includes(q) ||
    (d.defectNumber || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => downloadCsv('defects.csv',
      ['Defect #', 'Vessel', 'Description', 'Status', 'Date Observed'],
      filtered.map((d) => [d.defectNumber || `#${d.id}`, d.vesselName || '', d.description || '', d.status || '', d.dateObserved ? new Date(d.dateObserved).toLocaleDateString() : '']),
    )},
    { label: 'View Defects', variant: 'primary', onClick: () => { onClose(); navigate('/operations/defects-list') } },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title={title} wide
      actions={actions.filter(action => action.label !== 'Download CSV' || (permissionsLoaded && opCan('READ_QHSE_DEFECT')))}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel, description or defect #...'
    >
      {overdue && <p className='text-muted'>Open defects observed more than 30 days ago. Drafts, closed and office-reviewed defects are excluded. No due date is supplied by the source.</p>}
      <div className='table-responsive'>
        <table className='table table-sm align-middle mb-0'>
          <thead>
            <tr style={{ fontSize: '10px' }} className='text-muted text-uppercase'>
              <th className='fw-semibold'>Defect #</th>
              <th className='fw-semibold'>Vessel</th>
              <th className='fw-semibold'>Description</th>
              <th className='fw-semibold'>Status</th>
              <th className='fw-semibold'>Date Observed</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '11px' }}>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className='text-center text-muted py-4'>No defects found</td></tr>
            ) : (
              filtered.map((d, i) => (
                <tr key={i}>
                  <td className='fw-semibold text-primary'>{d.defectNumber || `#${d.id}`}</td>
                  <td className='fw-semibold text-dark'>{d.vesselName || '—'}</td>
                  <td className='text-muted'>{d.description || '—'}</td>
                  <td>
                    <span className={`badge ${d.status === 'SUBMIT' ? 'badge-light-danger' : d.status === 'SAVE' ? 'badge-light-warning' : 'badge-light-secondary'}`} style={{ fontSize: '9px' }}>
                      {d.status}
                    </span>
                  </td>
                  <td className='text-muted'>{d.dateObserved ? new Date(d.dateObserved).toLocaleDateString() : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ────────────── DPR Detail Modal ────────────── */
type DprDetailModalProps = {
  open: boolean
  onClose: () => void
  dprList: any[]
  title?: string
}
const DprDetailModal: FC<DprDetailModalProps> = ({ open, onClose, dprList, title = 'DPR Summary' }) => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = dprList.filter((d: any) =>
    (d.vesselName || d.vessel?.fleet_name || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => downloadCsv('dpr-summary.csv',
      ['Vessel', 'Date', 'Status', 'Port/Sea', 'Activities'],
      filtered.map((d: any) => [d.vesselName || d.vessel?.fleet_name || '', d.reportDate ? new Date(d.reportDate).toLocaleDateString() : '', d.status || '', d.portOrSea || (typeof d.atSea === 'boolean' ? d.atSea ? 'SEA' : 'PORT' : 'Unknown'), Number.isFinite(d.activityCount) ? `${d.activityCount} activities` : '']),
    )},
  ]

  actions.push({ label: 'Go to Daily Position Reports', variant: 'primary', onClick: () => { onClose(); navigate(DPR_ROUTE) } })

  return (
    <DashboardModal open={open} onClose={onClose} title={title} wide
      actions={actions.filter(action => action.label !== 'Download CSV' || (permissionsLoaded && opCan(OP.DOWNLOAD_DAILY_PROGRESS_REPORT)))}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel...'
    >
      <div className='table-responsive'>
        <table className='table table-sm align-middle mb-0'>
          <thead>
            <tr style={{ fontSize: '10px' }} className='text-muted text-uppercase'>
              <th className='fw-semibold'>Vessel</th>
              <th className='fw-semibold'>Date</th>
              <th className='fw-semibold'>Status</th>
              <th className='fw-semibold'>Port/Sea</th>
              <th className='fw-semibold'>Activities</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '11px' }}>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className='text-center text-muted py-4'>No DPRs found</td></tr>
            ) : (
              filtered.map((d: any, i: number) => (
                <tr key={i}>
                  <td className='fw-semibold text-dark'>{d.vesselName || d.vessel?.fleet_name || '—'}</td>
                  <td>{d.reportDate ? new Date(d.reportDate).toLocaleDateString() : '—'}</td>
                  <td>
                    <span className={`badge ${d.status === 'SUBMITTED' ? 'badge-light-success' : 'badge-light-warning'}`} style={{ fontSize: '9px' }}>{d.status}</span>
                  </td>
                  <td className='text-muted'>{d.portOrSea || (typeof d.atSea === 'boolean' ? d.atSea ? 'SEA' : 'PORT' : 'Unknown')}</td>
                  <td className='text-muted'>{Number.isFinite(d.activityCount) ? `${d.activityCount} activities` : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

const PortConsumptionModal: FC<{ open: boolean; onClose: () => void; rows: PortConsumptionRow[] }> = ({ open, onClose, rows }) => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const query = search.trim().toLowerCase()
  const filtered = rows.filter(row => ['Daily Position Report', row.vessel, row.date, String(row.reportId)].some(value => value.toLowerCase().includes(query)))
  const actions: ActionButton[] = []
  if (permissionsLoaded && opCan(OP.DOWNLOAD_DAILY_PROGRESS_REPORT)) actions.push({ label: 'Download CSV', onClick: () => downloadCsv('in-port-dpr-consumption.csv',
    ['Vessel', 'Report', 'Report ID', 'Date', 'Status', 'Berth fuel (KL)', 'Berth hours', 'Anchorage fuel (KL)', 'Anchorage hours'],
    filtered.map(row => [row.vessel, 'Daily Position Report', String(row.reportId), row.date, row.status, ...[row.berthFuel, row.berthHours, row.anchorageFuel, row.anchorageHours].map(value => value == null ? '' : String(value))]),
  ) })
  actions.push({ label: 'Go to Daily Position Reports', variant: 'primary', onClick: () => { onClose(); navigate(DPR_ROUTE) } })
  return <DashboardModal open={open} onClose={onClose} title='In-Port Consumption' wide actions={actions}
    searchQuery={search} onSearchChange={setSearch} searchPlaceholder='Search vessel, report name, recorded date or DPR number...'>
    <p className='text-muted'>Recorded berth and anchorage usage from submitted/reviewed DPRs. Fuel is in KL; duration is in hours. Each saved row is listed separately.</p>
    <PortConsumptionTable rows={filtered} />
  </DashboardModal>
}

/* ────────────── Cargo Detail Modal ────────────── */
type CargoDetailModalProps = {
  open: boolean
  onClose: () => void
  cargoOps: any[]
}
const CargoDetailModal: FC<CargoDetailModalProps> = ({ open, onClose, cargoOps }) => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = cargoOps.filter((c: any) =>
    (c.vesselName || '').toLowerCase().includes(q) ||
    (c.voyageNumber || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => downloadCsv('cargo-operations.csv',
      ['Vessel', 'Voyage', 'Type', 'Quantity (MT)', 'Ports'],
      filtered.map((c: any) => [c.vesselName || '', c.voyageNumber || '', c.breakupType || '', c.totalCargoQtyMt != null ? Number(c.totalCargoQtyMt).toLocaleString() : '', c.loadPorts || c.dischargePorts || '']),
    )},
  ]

  actions.push({ label: 'Go to Cargo Operations', variant: 'primary', onClick: () => { onClose(); navigate('/operations/cargo-operations') } })

  return (
    <DashboardModal open={open} onClose={onClose} title='Cargo Operations' wide
      actions={actions.filter(action => action.label !== 'Download CSV' || (permissionsLoaded && opCan(OP.DOWNLOAD_CARGO_OPERATION)))}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel or voyage...'
    >
      <div className='table-responsive'>
        <table className='table table-sm align-middle mb-0'>
          <thead>
            <tr style={{ fontSize: '10px' }} className='text-muted text-uppercase'>
              <th className='fw-semibold'>Vessel</th>
              <th className='fw-semibold'>Voyage</th>
              <th className='fw-semibold'>Type</th>
              <th className='fw-semibold'>Quantity (MT)</th>
              <th className='fw-semibold'>Ports</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '11px' }}>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className='text-center text-muted py-4'>No cargo operations found</td></tr>
            ) : (
              filtered.map((c: any, i: number) => (
                <tr key={i}>
                  <td className='fw-semibold text-dark'>{c.vesselName || '—'}</td>
                  <td>{c.voyageNumber || '—'}</td>
                  <td>
                    <span className={`badge ${c.breakupType === 'TANK' ? 'badge-light-primary' : 'badge-light-success'}`} style={{ fontSize: '9px' }}>{c.breakupType}</span>
                  </td>
                  <td className='fw-semibold'>{c.totalCargoQtyMt != null ? Number(c.totalCargoQtyMt).toLocaleString() : '—'}</td>
                  <td className='text-muted'>{c.loadPorts || c.dischargePorts || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ────────────── Work Queue Modal ────────────── */
type WorkQueueModalProps = {
  open: boolean
  onClose: () => void
  createdReports: any[]
  defects: QHSEDefectDto[]
}
const WorkQueueModal: FC<WorkQueueModalProps> = ({ open, onClose, createdReports, defects }) => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()

  const draftReports = createdReports.filter((r: any) => r.status === 'DRAFT').filter((r: any) =>
    dashboardReportName(r).toLowerCase().includes(q) || (r.vesselName || '').toLowerCase().includes(q)
  )
  const openDefects = defects.filter(isOpenDashboardDefect).filter((d) =>
    (d.vesselName || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => {
      const rows: string[][] = []
      draftReports.forEach((r: any) => rows.push(['Draft', dashboardReportName(r), r.vesselName || 'Vessel name unavailable', r.createdDateTime ? `${Math.round((Date.now() - new Date(r.createdDateTime).getTime()) / (1000 * 60 * 60))}h` : '']))
      openDefects.forEach((d) => rows.push(['Defect', d.defectNumber || `#${d.id}`, d.vesselName || '', d.dateObserved ? `${Math.round((Date.now() - new Date(d.dateObserved).getTime()) / (1000 * 60 * 60 * 24))}d` : '']))
      downloadCsv('work-queue.csv', ['Type', 'Item', 'Vessel', 'Age'], rows)
    }},
  ]

  actions.push({ label: 'Go to Position Reports', onClick: () => { onClose(); navigate('/operations/overview') } }, { label: 'Go to Defects List', onClick: () => { onClose(); navigate('/operations/defects-list') } })

  return (
    <DashboardModal open={open} onClose={onClose} title='Actionable Work Queue' wide
      actions={actions.filter(action => action.label !== 'Download CSV' || (permissionsLoaded && (!draftReports.length || opCan(OP.DOWNLOAD_POSITION_REPORT)) && (!openDefects.length || opCan('READ_QHSE_DEFECT'))))}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel or description...'
    >
      <div className='d-flex flex-column gap-3'>
        <h6 className='text-muted text-uppercase mb-0' style={{ fontSize: '10px' }}>Draft Reports</h6>
        {draftReports.length === 0 ? (
          <div className='text-muted text-center py-3' style={{ fontSize: '11px' }}>No draft reports</div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-sm align-middle mb-0'>
              <thead><tr style={{ fontSize: '10px' }} className='text-muted text-uppercase'>
                <th className='fw-semibold'>Type</th><th className='fw-semibold'>Vessel</th><th className='fw-semibold'>Created</th><th className='fw-semibold'>Age</th>
              </tr></thead>
              <tbody style={{ fontSize: '11px' }}>
                {draftReports.map((r: any, i: number) => (
                  <tr key={i}>
                    <td>{dashboardReportName(r)}</td>
                    <td>{r.vesselName || 'Vessel name unavailable'}</td>
                    <td className='text-muted'>{r.createdDateTime ? new Date(r.createdDateTime).toLocaleDateString() : '—'}</td>
                    <td className='text-muted'>{r.createdDateTime ? `${Math.round((Date.now() - new Date(r.createdDateTime).getTime()) / (1000 * 60 * 60))}h` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <h6 className='text-muted text-uppercase mb-0 mt-2' style={{ fontSize: '10px' }}>Open Defects</h6>
        {openDefects.length === 0 ? (
          <div className='text-muted text-center py-3' style={{ fontSize: '11px' }}>No open defects</div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-sm align-middle mb-0'>
              <thead><tr style={{ fontSize: '10px' }} className='text-muted text-uppercase'>
                <th className='fw-semibold'>Defect #</th><th className='fw-semibold'>Vessel</th><th className='fw-semibold'>Description</th><th className='fw-semibold'>Age</th>
              </tr></thead>
              <tbody style={{ fontSize: '11px' }}>
                {openDefects.map((d, i) => (
                  <tr key={i}>
                    <td className='fw-semibold text-primary'>{d.defectNumber || `#${d.id}`}</td>
                    <td className='fw-semibold'>{d.vesselName || '—'}</td>
                    <td className='text-muted'>{(d.description || '—').slice(0, 50)}</td>
                    <td className='text-muted'>{d.dateObserved ? `${Math.round((Date.now() - new Date(d.dateObserved).getTime()) / (1000 * 60 * 60 * 24))}d` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardModal>
  )
}

/* ────────────── Critical Events Modal ────────────── */
type CriticalEventsModalProps = {
  open: boolean
  onClose: () => void
  voyages: DashboardVoyage[]
  onViewVoyage: (id: number) => void
}
const CriticalEventsModal: FC<CriticalEventsModalProps> = ({ open, onClose, voyages, onViewVoyage }) => {
  const navigate = useNavigate()
  const [calendar, setCalendar] = useState(false)
  const [date, setDate] = useState('')
  const events = upcomingDashboardVoyages(voyages)
  const filtered = date ? events.filter(v => v.eta?.slice(0, 10) === date) : events
  const actions: ActionButton[] = [
    { label: calendar ? 'View List' : 'View Calendar', onClick: () => { setCalendar(!calendar); setDate('') } },
    { label: 'Go to Voyages', variant: 'primary', onClick: () => { onClose(); navigate('/operations/voyages') } },
  ]
  return <DashboardModal open={open} onClose={onClose} title='Upcoming Critical Events' actions={actions} wide>
    <p className='text-muted'>Saved arrival ETAs for active voyages, nearest first. These are schedule events; risk severity is not assessed.</p>
    {calendar && <div className='d-flex gap-2 align-items-center mb-3'>
      <label htmlFor='dashboard-event-date'>Arrival date</label>
      <input id='dashboard-event-date' type='date' value={date} onChange={e => setDate(e.target.value)} />
      <button className='btn btn-sm btn-light' onClick={() => setDate('')}>Show all dates</button>
    </div>}
    <div aria-live='polite'>{filtered.length} upcoming event{filtered.length === 1 ? '' : 's'}</div>
    {filtered.length === 0 ? <p className='text-muted py-3'>No saved future arrival ETAs for this selection.</p> : filtered.map(v => <div key={v.id} className='border rounded p-3 mt-2 d-flex justify-content-between gap-3'>
      <div><time dateTime={v.eta}>{new Date(v.eta!).toLocaleString()}</time>
        <div className='fw-semibold'>{v.vessel?.fleet_name || 'Vessel'} · {v.voyageNumber || `Voyage #${v.id}`}</div>
        <div>Planned arrival: {v.arrivalPort || 'Port not reported'}</div>
      </div>
      <button className='btn btn-sm btn-light-primary' onClick={() => onViewVoyage(v.id)}>View Voyage</button>
    </div>)}
  </DashboardModal>
}

/* ────────────── Insights Modal ────────────── */
type InsightsModalProps = {
  open: boolean
  onClose: () => void
  insights: { icon: string; color: string; text: string }[]
}
const InsightsModal: FC<InsightsModalProps> = ({ open, onClose, insights }) => {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const actions: ActionButton[] = insights.length > 0 && permissionsLoaded && [OP.DOWNLOAD_VOYAGE, OP.DOWNLOAD_POSITION_REPORT, 'READ_QHSE_DEFECT'].every(opCan)
    ? [{ label: 'Download Insights', onClick: () => {
      const rows = insights.map((ins) => [ins.text.replace(/<[^>]*>/g, '')])
      downloadCsv('operations-insights.csv', ['Insight'], rows)
    }}]
    : []

  return (
    <DashboardModal open={open} onClose={onClose} title='Operations Insights' actions={actions.length > 0 ? actions : undefined}>
      <div className='d-flex flex-column gap-3'>
        {insights.length === 0 ? (
          <div className='text-muted text-center py-4' style={{ fontSize: '11px' }}>No insights available</div>
        ) : (
          insights.map((ins, i) => (
            <div key={i} className='d-flex align-items-start gap-3 p-3 rounded border' style={{ borderColor: '#eee' }}>
              <div className={`rounded d-flex align-items-center justify-content-center flex-shrink-0 bg-light-${ins.color}`} style={{ width: '32px', height: '32px' }}>
                <KtsvgIcon name={ins.icon} color={ins.color} />
              </div>
              <div className='flex-1'>
                <div className='text-dark' style={{ fontSize: '11px', lineHeight: '1.4' }}>{ins.text}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardModal>
  )
}

/* ────────────── KPI Detail Modal ────────────── */
type KpiDetailModalProps = {
  open: boolean
  onClose: () => void
  kpis: any
  destination?: { label: string; path: string }
  metrics?: SummaryMetric[]
  canDownload?: boolean
  title?: string
}
const KpiDetailModal: FC<KpiDetailModalProps> = ({ open, onClose, kpis, destination, metrics, canDownload = false, title = 'KPI Summary' }) => {
  const navigate = useNavigate()
  const items: SummaryMetric[] = metrics ?? [
    { label: 'Active Voyages', value: kpis.activeVoyages || 0 },
    { label: 'At Sea', value: kpis.vesselsAtSea || 0 },
    { label: 'In Port', value: kpis.vesselsInPort || 0 },
    { label: 'Open Defects', value: kpis.openDefects || 0 },
    { label: 'CII Rating', value: kpis.ciiRating || '—' },
    { label: 'DPR Reports', value: kpis.draftDprReports || 0 },
  ]

  const actions: ActionButton[] = canDownload ? [
    { label: 'Download Summary', onClick: () => downloadCsv('ops-kpi-summary.csv',
      ['Metric', 'Value', 'Source / limitation'],
      items.map((i) => [i.label, String(i.value), i.detail || '']),
    )},
  ] : []
  if (destination) {
    actions.push({ label: destination.label, variant: 'primary', onClick: () => {
      onClose()
      navigate(destination.path)
    } })
  }

  return (
    <DashboardModal open={open} onClose={onClose} title={title} actions={actions}>
      <div className='row g-3'>
        {items.map((item, i) => (
          <div key={i} className='col-6'>
            <div className='fw-bold text-dark' style={{ fontSize: '18px' }}>{item.value}</div>
            <div className='text-muted' style={{ fontSize: '10px' }}>{item.label}</div>
            {item.detail && <div className='text-muted mt-1' style={{ fontSize: '11px' }}>{item.detail}</div>}
          </div>
        ))}
      </div>
    </DashboardModal>
  )
}

/* ────────────── KTSVG helper (inline for InsightsModal) ────────────── */
const KtsvgIcon: FC<{ name: string; color: string }> = ({ name, color }) => {
  const icons: Record<string, string> = {
    'trending-up': '/media/icons/duotune/general/gen001.svg',
    'alert-circle': '/media/icons/duotune/abstract/abs042.svg',
    fuel: '/media/icons/duotune/general/gen034.svg',
    'trending-down': '/media/icons/duotune/general/gen002.svg',
    clock: '/media/icons/duotune/general/gen018.svg',
  }
  return (
    <img src={icons[name] || icons['trending-up']} alt='' className={`svg-icon-2 text-${color}`} style={{ width: '18px', height: '18px' }} />
  )
}

export {
  PortConsumptionModal,
  VoyageDetailModal,
  DefectDetailModal,
  DprDetailModal,
  CargoDetailModal,
  WorkQueueModal,
  CriticalEventsModal,
  InsightsModal,
  KpiDetailModal,
}
